import {
  cn,
  injectDataCy,
  KdsBadge,
  KdsButtonIcon,
  KdsCheckbox,
  KdsFocusableElement,
  KdsText,
} from "@kazaar-platform/design-system";
import {
  RiDeleteBinLine,
  RiEditLine,
  RiStarFill,
  RiStarLine,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { parsePhoneNumber } from "libphonenumber-js";
import { isEmpty, isNil } from "lodash";
import { useCallback, useMemo } from "react";

import { type CardAddressProps } from "./CardAddress.types";

export const CardAddress = ({
  addressDetails,
  isUsersDefaultAddress,
  isSelected,
  isPressable,
  isNew,
  isDeleted,
  isWithCheckbox,
  onCardPress,
  onDelete,
  onEdit,
  onChangeDefaultAddress,
  overlay,
  className,
}: CardAddressProps) => {
  const { t } = useTranslate();

  const {
    name,
    recipientName,
    address,
    complementaryInfo,
    postalCode,
    city,
    country,
    phoneNumber,
  } = addressDetails;

  const containerStyle = cn(
    "rounded-lg border border-outlined-border p-sp-md gap-sp-sm overflow-x-hidden relative",
    isSelected && "border-outlined-borderprimary",
    isPressable && "cursor-pointer",
    isDeleted && "!bg-bg-disabled",
    isNew && "!border-warning-main",
    className
  );

  // Define Text Color
  const getTextColor = () => {
    if (isDeleted) {
      return "text-bodyvariant";
    }
    if (isNew) {
      return "warning-text";
    }
    return "text-body";
  };

  // Format phone number
  const formattedPhoneNumber = useMemo<string | undefined>(() => {
    if (!isNil(phoneNumber) && !isEmpty(phoneNumber)) {
      try {
        const parsedPhoneNumber = parsePhoneNumber(phoneNumber);

        if (parsedPhoneNumber) {
          return parsedPhoneNumber.formatNational();
        }
        return phoneNumber;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Error while parsing phone number", error);
        }
        return phoneNumber;
      }
    }
  }, [phoneNumber]);

  const handlePress = useCallback(() => {
    if (isPressable && onCardPress) {
      onCardPress();
    }
  }, [isPressable, onCardPress]);

  return (
    <KdsFocusableElement
      className={containerStyle}
      onClick={handlePress}
      {...injectDataCy("cardAddress")}
    >
      {overlay}
      <div className="flex items-start gap-sp-md">
        {isWithCheckbox && (
          <div className="mt-1">
            <KdsCheckbox isChecked={isSelected} onChange={handlePress} />
          </div>
        )}

        <div className="flex flex-1 flex-col gap-sp-xs">
          <div className="flex flex-row items-start justify-between gap-sp-md">
            <div className="flex flex-row items-center gap-sp-md">
              <KdsText size="title-sm" color={getTextColor()}>
                {name}
              </KdsText>
              {isUsersDefaultAddress && (
                <KdsBadge iconLeft={RiStarFill} size="sm">
                  {t("cardAddress.defaultLabel")}
                </KdsBadge>
              )}
            </div>
            <div className="flex gap-sp-xs">
              {isNew && (
                <KdsBadge size="sm" state="warning">
                  {t("cardAddress.added")}
                </KdsBadge>
              )}
              {isDeleted && (
                <KdsBadge size="sm" state="neutral">
                  {t("cardAddress.deleted")}
                </KdsBadge>
              )}
              {onChangeDefaultAddress && !isUsersDefaultAddress && (
                <KdsButtonIcon
                  icon={RiStarLine}
                  variant="ghost"
                  color="neutral"
                  size="small"
                  onPress={onChangeDefaultAddress}
                />
              )}
              {onEdit && (
                <KdsButtonIcon
                  icon={RiEditLine}
                  variant="outlined"
                  color="neutral"
                  size="small"
                  onPress={onEdit}
                />
              )}
              {onDelete && (
                <KdsButtonIcon
                  icon={RiDeleteBinLine}
                  variant="outlined"
                  color="error"
                  size="small"
                  onPress={onDelete}
                />
              )}
            </div>
          </div>

          <div className="grid w-full grid-cols-12 items-start">
            <div className="col-span-6 flex flex-col gap-sp-xs">
              <div>
                <KdsText size="caption-sm" color="text-bodyvariant">
                  {t("cardAddress.recipient")}
                </KdsText>
                <KdsText
                  size="subtitle-sm"
                  color={isDeleted ? "text-bodyvariant" : "text-body"}
                >
                  {recipientName ? recipientName : "-"}
                </KdsText>
              </div>

              <div>
                <KdsText size="caption-sm" color="text-bodyvariant">
                  {t("cardAddress.phone")}
                </KdsText>
                <KdsText
                  size="subtitle-sm"
                  color={isDeleted ? "text-bodyvariant" : "text-body"}
                >
                  {formattedPhoneNumber ? formattedPhoneNumber : "-"}
                </KdsText>
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-sp-xs">
              <div>
                <KdsText size="caption-sm" color="text-bodyvariant">
                  {t("cardAddress.address")}
                </KdsText>
                {!!address && (
                  <KdsText
                    size="subtitle-sm"
                    color={isDeleted ? "text-bodyvariant" : "text-body"}
                  >
                    {address}
                  </KdsText>
                )}
                {!!complementaryInfo && (
                  <KdsText
                    size="subtitle-sm"
                    color={isDeleted ? "text-bodyvariant" : "text-body"}
                  >
                    {complementaryInfo}
                  </KdsText>
                )}
                {!!postalCode && !!city && (
                  <KdsText
                    size="subtitle-sm"
                    color={isDeleted ? "text-bodyvariant" : "text-body"}
                  >{`${postalCode} ${city}`}</KdsText>
                )}
                {!!country && (
                  <KdsText
                    size="subtitle-sm"
                    color={isDeleted ? "text-bodyvariant" : "text-body"}
                  >
                    {country ?? "-"}
                  </KdsText>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </KdsFocusableElement>
  );
};
